export interface IBaseStoreState<T> {
    payload: T;
    error?: string | Error;
}

export interface IErrorResponse {
    error: string | Error;
}

export interface IResponse<T = any> {
    payload: T;
    meta?: any;
    nextPage?: string;
    hasNextPage?: boolean;
    data?: T; // old app way to pass params
}

export interface IFetchStoreState<T> extends IBaseStoreState<T> {
    isFetching: boolean;
    nextPage?: string;
    hasNextPage?: boolean;
    meta?: any;
    isUpdating?: boolean;
}

export interface IUpdateStoreState<T> extends IBaseStoreState<T> {
    isUpdating: boolean;
}

export interface IUpdatable {
    UPDATE: string;
}

export interface IProgressive {
    START: string;
    SUCCESS: string;
    ERROR: string;
    CANCEL: string;
}

export const initialFetchState = {
    isFetching: false,
    payload: null,
};

export const initialUpdateState = {
    isUpdating: false,
    payload: null,
};

export const initialBaseState = {
    ...initialFetchState,
    ...initialUpdateState,
};

export const updateActions = <T>(TYPE: IUpdatable) => ({
    [TYPE.UPDATE]: (state: IBaseStoreState<T>, { payload }: IBaseStoreState<T>): IBaseStoreState<T> => ({
        ...state,
        payload,
    }),
});

/**
 * Actions for updating full state at once. Could be useful when state is complex.
 */
export const updateStateActions = <S>(TYPE: IUpdatable) => ({
    [TYPE.UPDATE]: (state: S, { payload }: { payload: Partial<S> }): S => ({
        ...state,
        ...payload,
    }),
});

export const progressActions = <T, V extends IBaseStoreState<T>>(TYPE: IProgressive, field: string) => ({
    [TYPE.START]: (state: V): V => ({
        ...state,
        error: null,
        [field]: true,
    }),
    [TYPE.SUCCESS]: (state: V, { data, payload }: IResponse<T>): V => ({
        ...state,
        payload: data || payload,
        error: null,
        [field]: false,
    }),
    [TYPE.ERROR]: (state: V, { error }: IErrorResponse): V => ({
        ...state,
        error,
        [field]: false,
    }),
});

export const updateProgressActions = <T>(TYPE: IProgressive) =>
    progressActions<T, IUpdateStoreState<T>>(TYPE, "isUpdating");

export const fetchActions = <T>(TYPE: IProgressive) => ({
    ...progressActions<T, IFetchStoreState<T>>(TYPE, "isFetching"),
    [TYPE.SUCCESS]: (
        state: IFetchStoreState<T>,
        { data, payload, meta, nextPage, hasNextPage }: IResponse<T>,
    ): IFetchStoreState<T> => ({
        ...state,
        payload: data || payload,
        nextPage,
        hasNextPage: hasNextPage ?? !!nextPage,
        meta,
        error: null,
        isFetching: false,
    }),
});
